import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Combobox, Listbox, BlockStack, InlineStack, Text, Icon, LegacyStack, Tag, EmptySearchResult, AutoSelection } from '@shopify/polaris';
import { SearchIcon, PlusCircleIcon } from '@shopify/polaris-icons';

import { FetchCustom } from './FetchCustom';
import { useHostNavigate } from "../hooks";

export function ShippingMethodPickerCustom(props) {
    const navigate = useHostNavigate();
    const fetch = FetchCustom();

    const [loading, setLoading] = useState(false);
    const [redirectUrl, setRedirectUrl] = useState('');
    const [list, setList] = useState([]);
    const [selectedTags, setSelectedTags] = useState(props.value ? JSON.parse(props.value) : []);
    const [value, setValue] = useState('');
    const [suggestion, setSuggestion] = useState('');

    const load = async () => {
        setLoading(true);

        const data = await fetch('api/home/shippingMethods', {});
        if (data.redirectUrl) {
            setRedirectUrl(data.redirectUrl);
        }
        else {
            setList(data);
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (redirectUrl) {
            navigate(redirectUrl);
        }
    }, [redirectUrl, navigate]);

    const handleActiveOptionChange = useCallback(
        (activeOption) => {
            const activeOptionIsAction = activeOption === value;

            if (!activeOptionIsAction && !selectedTags.includes(activeOption)) {
                setSuggestion(activeOption);
            } else {
                setSuggestion('');
            }
        },
        [value, selectedTags],
    );
    const updateSelection = useCallback(
        (selected) => {
            const nextSelectedTags = new Set([...selectedTags]);

            if (nextSelectedTags.has(selected)) {
                nextSelectedTags.delete(selected);
            } else {
                nextSelectedTags.add(selected);
            }
            const stags = [...nextSelectedTags];
            setSelectedTags(stags);
            setValue('');
            setSuggestion('');
            props.handleChange('shippingMethods', stags.length ? JSON.stringify(stags) : null);
        },
        [selectedTags, props],
    );

    const removeTag = useCallback(
        (tag) => () => {
            updateSelection(tag);
        },
        [updateSelection],
    );

    const getAllTags = useCallback(() => {
        return [...new Set([...list, ...selectedTags])];
    }, [list, selectedTags]);

    const formatOptionText = useCallback(
        (option) => {
            const trimValue = value.trim().toLocaleLowerCase();
            const matchIndex = option.toLocaleLowerCase().indexOf(trimValue);

            if (!value || matchIndex === -1) return option;

            const start = option.slice(0, matchIndex);
            const highlight = option.slice(matchIndex, matchIndex + trimValue.length);
            const end = option.slice(matchIndex + trimValue.length, option.length);

            return (
                <p>
                    {start}
                    <Text fontWeight="bold" as="span">
                        {highlight}
                    </Text>
                    {end}
                </p>
            );
        },
        [value],
    );

    const escapeSpecialRegExCharacters = useCallback(
        (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
        [],
    );

    const options = useMemo(() => {
        let list;
        const allTags = getAllTags();
        const filterRegex = new RegExp(escapeSpecialRegExCharacters(value), 'i');

        if (value) {
            list = allTags.filter((tag) => tag.match(filterRegex));
        } else {
            list = allTags;
        }

        return [...list];
    }, [value, getAllTags, escapeSpecialRegExCharacters]);

    const noResults = value && !getAllTags().includes(value);

    const optionMarkup =
        options.length > 0
            ? options.map((option) => {
                return (
                    <Listbox.Option
                        key={option}
                        value={option}
                        selected={selectedTags.includes(option)}
                        accessibilityLabel={option}
                        divider={noResults && options.indexOf(option) === options.length - 1}
                    >
                        <Listbox.TextOption selected={selectedTags.includes(option)}>
                            {formatOptionText(option)}
                        </Listbox.TextOption>
                    </Listbox.Option>
                );
            })
            : null;

    const actionMarkup = noResults ? (
        <Listbox.Action value={value}>
            <LegacyStack spacing="tight">
                <Icon source={PlusCircleIcon} tone="base" />
                <div>{`Add "${value}"`}</div>
            </LegacyStack>
        </Listbox.Action>
    ) : null;

    const emptyStateMarkup = optionMarkup ? null : (
        <EmptySearchResult
            title=""
            description={`No tags found matching "${value}"`}
        />
    );

    const loadingMarkup = loading ? (
        <Listbox.Loading accessibilityLabel="Loading" />
    ) : null;

    const listboxMarkup =
        optionMarkup || actionMarkup || emptyStateMarkup || loadingMarkup ? (
            <Listbox
                autoSelection={AutoSelection.None}
                onSelect={updateSelection}
                onActiveOptionChange={handleActiveOptionChange}
            >
                {optionMarkup && !loading ? optionMarkup : null}
                {actionMarkup && !loading ? actionMarkup : null}
                {loadingMarkup}
            </Listbox>
        ) : null;

    return (
        <BlockStack gap="300">
            <Combobox
                allowMultiple
                activator={
                    <Combobox.TextField
                        autoComplete="off"
                        label="Search shipping methods"
                        labelHidden
                        prefix={<Icon source={SearchIcon} tone="subdued" />}
                        error={props.error}
                        value={value}
                        suggestion={suggestion}
                        placeholder="Search shipping methods"
                        onChange={setValue}
                    />
                }
            >
                {listboxMarkup}
            </Combobox>
            {
                selectedTags.length ?
                    <InlineStack gap="100">
                        {
                            selectedTags.map((tag) => (
                                <Tag key={`option-${tag}`} onRemove={removeTag(tag)}>
                                    {tag}
                                </Tag>
                            ))
                        }
                    </InlineStack>
                    : null
            }
        </BlockStack>
    );
}